import { useCallback, useEffect, useState } from 'react'

export interface UseTwoStepConfirmResult {
  /** The id currently armed for confirm, or null if none. */
  pendingId: string | null
  /** True iff the given id is currently armed. Prefer this over pendingId === id in JSX. */
  isPending: (id: string) => boolean
  /** Arm the given id. If a different id was armed, it is replaced. */
  arm: (id: string) => void
  /** Clear the armed id, if any. Called on cancel, success, or unmount. */
  reset: () => void
}

/**
 * Two-step in-app confirm pattern for delete/reset/destructive actions.
 *
 * v2.1 finding #83: iOS Chrome silently suppresses `window.confirm()` in
 * some event-handler contexts (WebKit dialog gating). This hook replaces
 * the blocking `confirm()` with a state + button-re-render pattern where
 * the same button flips to a confirm state on first tap and executes on
 * second tap. Mirrors the pattern first established in
 * `src/screens/lots/LotCostSection.tsx:142` (pendingDeleteId) and adopted
 * in `src/screens/Feed.tsx` and `src/screens/Biomass.tsx` α3b.
 *
 * Unmount safety: the armed state is cleared automatically when the host
 * component unmounts, to avoid orphaned pendingId when a popover closes
 * (e.g. `MortalityEventsPopover`) between the first tap and the second.
 */
export function useTwoStepConfirm(): UseTwoStepConfirmResult {
  const [pendingId, setPendingId] = useState<string | null>(null)

  const isPending = useCallback((id: string) => pendingId === id, [pendingId])
  const arm = useCallback((id: string) => setPendingId(id), [])
  const reset = useCallback(() => setPendingId(null), [])

  // Unmount cleanup — resets on host unmount to prevent orphaned pendingId.
  useEffect(() => reset, [reset])

  return { pendingId, isPending, arm, reset }
}
